import type { DataRecord, DataValue, GridType } from '../types';
import type { Dataset } from '../data/dataset';

export interface PathSeg {
  uniqueName: string;
  value: DataValue;
}

export interface MemberNode {
  uniqueName: string;
  value: DataValue;
  caption: string;
  level: number;
  path: PathSeg[];
  /** Full path key (used to look up aggregations). */
  key: string;
  children: Map<string, MemberNode>;
  /** Children in display order (set by the ordering pass). */
  orderedChildren: MemberNode[];
  leafCount: number;
}

/** Stable, collision-free token for a single member value. */
export function valueToken(value: DataValue): string {
  if (value == null) return ' ';
  if (value instanceof Date) return `d${value.getTime()}`;
  switch (typeof value) {
    case 'number':
      return `#${value}`;
    case 'boolean':
      return `b${value ? 1 : 0}`;
    default:
      return `s${value}`;
  }
}

const SEP = '';

export function pathKey(segments: PathSeg[]): string {
  return segments.map((s) => valueToken(s.value)).join(SEP);
}

/** All cumulative prefix keys of a value list, including the empty (grand total). */
export function prefixKeys(values: DataValue[]): string[] {
  const keys: string[] = [''];
  let acc = '';
  for (let i = 0; i < values.length; i++) {
    acc = i === 0 ? valueToken(values[i]!) : `${acc}${SEP}${valueToken(values[i]!)}`;
    keys.push(acc);
  }
  return keys;
}

/** Build the full member hierarchy for an axis from (already filtered) records. */
export function buildMemberTree(
  records: DataRecord[],
  fields: string[],
  dataset: Dataset,
): MemberNode[] {
  const roots = new Map<string, MemberNode>();
  if (!fields.length) return [];

  for (const record of records) {
    let parent = roots;
    const path: PathSeg[] = [];
    for (let level = 0; level < fields.length; level++) {
      const uniqueName = fields[level]!;
      const value = dataset.resolveValue(record, uniqueName);
      path.push({ uniqueName, value });
      const token = valueToken(value);
      let node = parent.get(token);
      if (!node) {
        node = {
          uniqueName,
          value,
          caption: dataset.memberCaption(uniqueName, value),
          level,
          path: path.map((p) => ({ ...p })),
          key: pathKey(path),
          children: new Map(),
          orderedChildren: [],
          leafCount: 0,
        };
        parent.set(token, node);
      }
      parent = node.children;
    }
  }
  return [...roots.values()];
}

/** Recompute leafCount from the ordered (post-filter) children. */
export function computeLeafCount(node: MemberNode): number {
  if (node.orderedChildren.length === 0) {
    node.leafCount = 1;
    return 1;
  }
  node.leafCount = node.orderedChildren.reduce((sum, c) => sum + computeLeafCount(c), 0);
  return node.leafCount;
}

export interface VisibleNode {
  node: MemberNode;
  expanded: boolean;
  /** Non-leaf node currently showing its subtotal. */
  isSubtotal: boolean;
}

export type IsExpanded = (node: MemberNode) => boolean;

/**
 * Compact layout: pre-order walk. Every node emits one line; an expanded parent
 * additionally emits its descendants. A collapsed/leaf node shows the aggregate
 * over all of its descendants.
 */
export function flattenCompact(roots: MemberNode[], isExpanded: IsExpanded): VisibleNode[] {
  const out: VisibleNode[] = [];
  const walk = (nodes: MemberNode[]) => {
    for (const node of nodes) {
      const hasChildren = node.orderedChildren.length > 0;
      const expanded = hasChildren && isExpanded(node);
      out.push({ node, expanded, isSubtotal: expanded });
      if (expanded) walk(node.orderedChildren);
    }
  };
  walk(roots);
  return out;
}

/**
 * Classic layout: leaves are emitted at full depth; each expanded group emits a
 * trailing subtotal line when requested.
 */
export function flattenClassic(
  roots: MemberNode[],
  isExpanded: IsExpanded,
  showSubtotals: boolean,
): VisibleNode[] {
  const out: VisibleNode[] = [];
  const walk = (nodes: MemberNode[]) => {
    for (const node of nodes) {
      const hasChildren = node.orderedChildren.length > 0;
      const expanded = hasChildren && isExpanded(node);
      if (!expanded) {
        out.push({ node, expanded: false, isSubtotal: false });
        continue;
      }
      walk(node.orderedChildren);
      if (showSubtotals) out.push({ node, expanded: true, isSubtotal: true });
    }
  };
  walk(roots);
  return out;
}

/** Flat layout: only the deepest leaves, no subtotals. */
export function flattenFlat(roots: MemberNode[]): VisibleNode[] {
  const out: VisibleNode[] = [];
  const walk = (nodes: MemberNode[]) => {
    for (const node of nodes) {
      if (node.orderedChildren.length === 0) {
        out.push({ node, expanded: false, isSubtotal: false });
      } else {
        walk(node.orderedChildren);
      }
    }
  };
  walk(roots);
  return out;
}

export function flatten(
  roots: MemberNode[],
  type: GridType,
  isExpanded: IsExpanded,
  showSubtotals: boolean,
): VisibleNode[] {
  switch (type) {
    case 'classic':
      return flattenClassic(roots, isExpanded, showSubtotals);
    case 'flat':
      return flattenFlat(roots);
    case 'compact':
    default:
      return flattenCompact(roots, isExpanded);
  }
}
